import { Box, Typography, useMediaQuery } from "@mui/material";
import { SectionHeading } from "@pages:app/audio/components/SectionHeading";
import { SpeakerTurn } from "@pages:app/audio/components/SpeakerTurn";
import { PlaybackPosition, TranscriptTurn } from "@pages:app/audio/util";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

interface TranscriptProps {
  turns: TranscriptTurn[];
  numbers: Map<string, number>;
  active?: PlaybackPosition | null;
  isPlaying?: boolean;
  onSeek?: (seconds: number) => void;
}

const ENTER_START_MS = 150;
const ENTER_STEP_MS = 45;

/*
 * The conversation as a list of speaker turns.
 * While the recording plays, the turn being spoken is kept in view.
 */
export const Transcript = ({
  turns,
  numbers,
  active = null,
  isPlaying = false,
  onSeek,
}: TranscriptProps) => {
  const { t } = useTranslation();
  const listRef = useRef<HTMLOListElement>(null);
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const activeTurn = active?.turn ?? null;

  useEffect(() => {
    if (!isPlaying || activeTurn === null || !listRef.current) return;
    const item = listRef.current.children.item(activeTurn);
    if (!item) return;

    const rect = item.getBoundingClientRect();
    const margin = 80;
    const visible =
      rect.top >= margin && rect.bottom <= window.innerHeight - margin;
    if (!visible) {
      item.scrollIntoView({
        block: "center",
        behavior: reducedMotion ? "auto" : "smooth",
      });
    }
  }, [activeTurn, isPlaying, reducedMotion]);

  return (
    <Box component="section" aria-labelledby="transcript-heading">
      <SectionHeading id="transcript-heading">
        {t("audio.transcript.title")}
      </SectionHeading>
      {turns.length === 0 ? (
        <Typography color="text.secondary">
          {t("audio.transcript.empty")}
        </Typography>
      ) : (
        <Box
          ref={listRef}
          component="ol"
          sx={{
            m: 0,
            p: 0,
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
          }}
        >
          {turns.map((turn, index) => (
            <SpeakerTurn
              key={index}
              turn={turn}
              number={
                turn.speaker !== null ? numbers.get(turn.speaker) : undefined
              }
              active={
                active?.turn === index
                  ? { line: active.line, word: active.word }
                  : null
              }
              onSeek={onSeek}
              enterDelayMs={
                reducedMotion
                  ? null
                  : ENTER_START_MS + Math.min(index, 12) * ENTER_STEP_MS
              }
            />
          ))}
        </Box>
      )}
    </Box>
  );
};
